/**
 * The filter builder's rows applied in memory, for a table that loads every
 * row it lists and filters without going back to the server. Each table
 * names its fields and how a row reads them (`FilterMatchers`); the helpers
 * here hold what one comparator means for a value of each type.
 */

import dayjs from "dayjs";

import { isDatePreset, type Filter } from "@/shared/domain/query/filters";

import { datePresetRange } from "./filterToCondition";

/** The filter builder's rows and how they combine. */
export interface InMemoryFilterParams {
  filters: readonly Filter[];
  /** AND requires every row, OR at least one. */
  matchMode: "AND" | "OR";
}

/** How each filterable field of `T` tests a row. */
export type FilterMatchers<T> = Record<
  string,
  (row: T, filter: Filter) => boolean
>;

function isNegative(comparator: string): boolean {
  return comparator === "NONE_OF" || comparator.startsWith("NOT_");
}

/** A presence row (`IS_NULL` / `IS_NOT_NULL`) against `value`; `undefined` for any other row. */
function matchesPresence(
  value: unknown,
  comparator: string,
): boolean | undefined {
  const unset = value === null || value === undefined || value === "";
  if (comparator === "IS_NULL") {
    return unset;
  }
  if (comparator === "IS_NOT_NULL") {
    return !unset;
  }
  return undefined;
}

/** An `enum` row: whether `value` is (or, negated, is not) a ticked option. */
export function matchesOptions(
  value: string | null | undefined,
  filter: Filter & { type: "enum" },
): boolean {
  const presence = matchesPresence(value, filter.comparator);
  if (presence !== undefined) {
    return presence;
  }
  const ticked = filter.values.some((option) => option.value === value);
  const unset = value === null || value === undefined;
  const matched = ticked || (unset && filter.includeNull === true);
  return isNegative(filter.comparator) ? !matched : matched;
}

/** A `boolean` row: whether `value` is one of the ticked values, or unset when "Not set" is. */
export function matchesBoolean(
  value: boolean | null | undefined,
  filter: Filter & { type: "boolean" },
): boolean {
  const presence = matchesPresence(value, filter.comparator);
  if (presence !== undefined) {
    return presence;
  }
  if (value === null || value === undefined) {
    return filter.includeNull === true;
  }
  return filter.values.includes(value);
}

/**
 * A LIKE pattern's tokens: one character, or the escape `\` with the
 * character it makes literal. A `\` that ends the pattern is a token alone.
 */
const LIKE_TOKENS = /\\.?|./gsu;

/** One token of a LIKE pattern as a regular expression. */
function likeToken(token: string): string {
  if (token === "%") {
    return ".*";
  }
  if (token === "_") {
    return ".";
  }
  const literal = token.startsWith("\\") ? token.slice(1) : token;
  return literal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * `pattern` as Postgres LIKE reads it with no ESCAPE clause: `%` any run of
 * characters, `_` any one, and `\` the escape that makes the next character
 * literal (`\%`, `\_`, `\\`). Null for a pattern that ends in a lone `\`,
 * which Postgres refuses once a row matches up to it, so it returns no row.
 */
function likePattern(pattern: string): RegExp | null {
  const tokens = pattern.match(LIKE_TOKENS) ?? [];
  if (tokens.at(-1) === "\\") {
    return null;
  }
  return new RegExp(`^${tokens.map(likeToken).join("")}$`, "i");
}

/** One positive string comparison, ignoring case. */
export function stringMatches(
  haystack: string,
  comparator: string,
  needle: string,
): boolean {
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  switch (comparator) {
    case "EQUALS":
    case "NOT_EQUALS":
    case "ANY_OF":
    case "NONE_OF":
      return h === n;
    case "CONTAINS":
    case "NOT_CONTAINS":
      return h.includes(n);
    case "LIKE":
    case "NOT_LIKE":
      return likePattern(needle)?.test(haystack) ?? false;
    default:
      return false;
  }
}

/**
 * A `string` row against `haystack`. A row with no values left after
 * trimming passes everything; a negated row passes when none of its values
 * match. `matches` replaces the positive comparison, for a field whose
 * values mean something else (a name prefix, say).
 */
export function matchesString(
  haystack: string | null | undefined,
  filter: Filter & { type: "string" },
  matches: (
    haystack: string,
    comparator: string,
    needle: string,
  ) => boolean = stringMatches,
): boolean {
  const presence = matchesPresence(haystack, filter.comparator);
  if (presence !== undefined) {
    return presence;
  }
  const needles = filter.values.filter((value) => value.trim() !== "");
  if (needles.length === 0) {
    return true;
  }
  const matched =
    haystack !== null &&
    haystack !== undefined &&
    needles.some((needle) => matches(haystack, filter.comparator, needle));
  return isNegative(filter.comparator) ? !matched : matched;
}

/** A `date` row against an ISO-8601 instant. Both edges are inclusive. */
export function matchesDate(
  instant: string | null | undefined,
  filter: Filter & { type: "date" },
): boolean {
  const presence = matchesPresence(instant, filter.comparator);
  if (presence !== undefined) {
    return presence;
  }
  if (instant === null || instant === undefined) {
    return false;
  }
  const time = dayjs(instant);
  if (isDatePreset(filter.comparator)) {
    const { start, end } = datePresetRange(filter.comparator);
    return !time.isBefore(start) && !time.isAfter(end);
  }
  const [first, second] = filter.values.map((value) => dayjs(value));
  switch (filter.comparator) {
    case "LESS_THAN":
      return time.isBefore(first);
    case "GREATER_THAN":
      return time.isAfter(first);
    case "BETWEEN":
      return !time.isBefore(first) && !time.isAfter(second);
    default:
      return false;
  }
}

/**
 * The rows that pass the filter builder's rows, joined by `matchMode`. No
 * rows pass everything; a row on a field `matchers` doesn't name matches
 * nothing.
 */
export function filterInMemory<T>(
  rows: readonly T[],
  { filters, matchMode }: InMemoryFilterParams,
  matchers: FilterMatchers<T>,
): T[] {
  if (filters.length === 0) {
    return [...rows];
  }
  const passes = (row: T, filter: Filter) =>
    Object.hasOwn(matchers, filter.field) &&
    matchers[filter.field](row, filter);
  return rows.filter((row) =>
    matchMode === "OR"
      ? filters.some((filter) => passes(row, filter))
      : filters.every((filter) => passes(row, filter)),
  );
}
